import React, { useState } from 'react';
import { useForm } from '@formspree/react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

// IMPORTANT: Replace this ID with the one Formspree gives you! (e.g. -> "xabcdegh")
const FORMSPREE_ID = "mdalvdoj";

const ContactForm = () => {
  const [state, handleSubmit] = useForm(FORMSPREE_ID);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    projectType: '',
    timeline: '',
    budget: '',
    message: ''
  });

  const projectTypes = [
    'Space Technology',
    'Remote Sensing / GIS',
    'Machine Learning / AI',
    'Computer Vision',
    'UI/UX Design',
    'Web Development',
    'Science Outreach / Workshop',
    'Research Collaboration',
    'Other'
  ];


  const timelineOptions = [
    'ASAP (Rush)',
    '1-2 weeks',
    '1 month',
    '2-3 months',
    '3-6 months',
    '6+ months',
    'Flexible'
  ];

  const budgetRanges = [
    'Below ₹20,000',
    '₹20,000 - ₹50,000',
    '₹50,000 - ₹1,00,000',
    '₹1,00,000+',
    'Let’s discuss'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (state.succeeded) {
    return (
      <div className="bg-success/10 border border-success/20 rounded-xl p-8 text-center relative overflow-hidden backdrop-blur-xl">
        <div className="absolute inset-0 bg-gradient-to-br from-success/5 via-transparent to-transparent opacity-30 pointer-events-none"></div>
        <div className="relative z-10">
          <div className="w-16 h-16 bg-success/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="CheckCircle" size={32} className="text-success-light" />
          </div>
          <h3 className="font-heading font-semibold text-xl text-success-light mb-2">
            Message Sent Successfully!
          </h3>
          <p className="text-text-secondary mb-4">
            Thanks for reaching out! I’ll get back within 24 hours to explore how we can collaborate—whether in tech, space, or beyond!
          </p>
        </div>
        <Button 
          variant="success" 
          onClick={() => window.location.reload()}
          className="mt-2 relative z-10"
        >
          Send Another Message
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl p-6 lg:p-8 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5 opacity-50 pointer-events-none"></div>
      <div className="relative z-10 mb-6">
        <h2 className="font-heading font-semibold text-2xl text-text-primary mb-2">
          Let’s Collaborate on Your Next STEM or Tech Idea!
        </h2>
        <p className="text-text-secondary">
          Whether it's space tech, web development, or research-based innovation—share your vision, and I’ll help shape it.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-text-secondary mb-2">
              Full Name *
            </label>
            <Input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="John Doe"
              required
              className="w-full bg-white/5 border-white/10 text-text-primary placeholder:text-text-muted transition-smooth focus:border-accent"
            />
          </div>
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-text-secondary mb-2">
              Email Address *
            </label>
            <Input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="john@company.com"
              required
              className="w-full bg-white/5 border-white/10 text-text-primary placeholder:text-text-muted transition-smooth focus:border-accent"
            />
          </div>
        </div>

        <div>
          <label htmlFor="company" className="block text-sm font-medium text-text-secondary mb-2">
            Company/Organization
          </label>
          <Input
            type="text"
            id="company"
            name="company"
            value={formData.company}
            onChange={handleInputChange}
            placeholder="Your Company Name"
            className="w-full bg-white/5 border-white/10 text-text-primary placeholder:text-text-muted transition-smooth focus:border-accent"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label htmlFor="projectType" className="block text-sm font-medium text-text-secondary mb-2">
              Project Type *
            </label>
            <select
              id="projectType"
              name="projectType"
              value={formData.projectType}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 border border-white/10 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent transition-smooth bg-white/5 text-text-primary"
            >
              <option value="" className="bg-background">Select Type</option>
              {projectTypes.map((type) => (
                <option key={type} value={type} className="bg-background">{type}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="timeline" className="block text-sm font-medium text-text-secondary mb-2">
              Timeline *
            </label>
            <select
              id="timeline"
              name="timeline"
              value={formData.timeline}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 border border-white/10 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent transition-smooth bg-white/5 text-text-primary"
            >
              <option value="" className="bg-background">Select Timeline</option>
              {timelineOptions.map((timeline) => (
                <option key={timeline} value={timeline} className="bg-background">{timeline}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="budget" className="block text-sm font-medium text-text-secondary mb-2">
              Budget Range
            </label>
            <select
              id="budget"
              name="budget"
              value={formData.budget}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border border-white/10 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent transition-smooth bg-white/5 text-text-primary"
            >
              <option value="" className="bg-background">Select Budget</option>
              {budgetRanges.map((budget) => (
                <option key={budget} value={budget} className="bg-background">{budget}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label htmlFor="message" className="block text-sm font-medium text-text-secondary mb-2">
            Project Details *
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleInputChange}
            placeholder="Share your project details — Is it research, tech development, or STEM outreach? What outcomes are you aiming for?"
            required
            rows={5}
            className="w-full px-4 py-3 border border-white/10 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent transition-smooth resize-vertical bg-white/5 text-text-primary placeholder:text-text-muted"
          />
        </div>

        <div className="flex flex-col sm:flex-row gap-4 pt-4">
          <Button
            variant="primary"
            type="submit"
            loading={state.submitting}
            iconName="Send"
            iconPosition="right"
            className="flex-1 sm:flex-none"
            aria-label="Send your project inquiry to Sudhan R"
            title="Send Message"
          >
            {state.submitting ? 'Sending...' : 'Send Message'}
          </Button>
          <p className="text-sm text-text-muted flex items-center">
            <Icon name="Shield" size={16} className="mr-2" />
            Your information is secure and will never be shared.
          </p>
        </div>
      </form>
    </div>
  );
};

export default ContactForm;